// Template grouping (prescription groups: migration 0036, 2026-11-02;
// investigation groups: migration 0037, 2026-11-04).
//
// Shared by the /prescription-templates + /investigations management pages
// and the EMR visit page's PrescriptionTemplatePickerDialog +
// InvestigationTemplatePickerDialog: all render a template library as
// collapsible per-group accordions with "Ungrouped" pinned last.

/** Accordion value for the pseudo-group holding templates with groupId null. */
export const UNGROUPED_KEY = "__ungrouped__";
export const UNGROUPED_LABEL = "Ungrouped";

/** Minimal group shape the bucketing needs (both group tables satisfy it). */
interface AnyGroup {
  id: string;
  name: string;
}

/** Minimal template shape the bucketing needs (both template tables satisfy it). */
interface AnyTemplate {
  groupId: string | null;
}

export interface TemplateBucket<T = any> {
  /** Accordion value: group id, or UNGROUPED_KEY for the pseudo-group. */
  key: string;
  /** Display name: group name, or UNGROUPED_LABEL. */
  name: string;
  /** The real groupId to PATCH onto a template moved here (null = ungroup). */
  groupId: string | null;
  items: T[];
}

/**
 * Bucket templates under their groups, alphabetically, "Ungrouped" last
 * (the investigations-page pattern). Generic since 2026-11-04 (migration
 * 0037): the same helper serves prescription and investigation templates.
 * Empty groups are HIDDEN — an accordion with no children is noise in the
 * picker. (The management pages keep empty groups inspectable in their
 * Groups tabs, which render from the plain groups list instead.)
 */
export function bucketTemplates<T extends AnyTemplate>(
  templates: T[],
  groups: AnyGroup[],
): TemplateBucket<T>[] {
  const byGroup = new Map<string, T[]>();
  for (const g of groups) byGroup.set(g.id, []);
  byGroup.set(UNGROUPED_KEY, []);
  for (const t of templates) {
    const key = t.groupId && byGroup.has(t.groupId) ? t.groupId : UNGROUPED_KEY;
    byGroup.get(key)!.push(t);
  }
  return [
    ...groups.map((g) => ({ key: g.id, name: g.name, groupId: g.id, items: byGroup.get(g.id)! })),
    { key: UNGROUPED_KEY, name: UNGROUPED_LABEL, groupId: null, items: byGroup.get(UNGROUPED_KEY)! },
  ].filter((b) => b.items.length > 0);
}